import React from "react";
import { createRoot } from "react-dom/client";
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bot,
  Check,
  Coins,
  KeyRound,
  Layers3,
  LockKeyhole,
  RefreshCcw,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Zap,
} from "lucide-react";
import "./styles.css";

const appUrl = "https://dca.tradebotsecrets.com";
const membershipUrl = `${appUrl}/subscription`;

const workflow = [
  {
    icon: KeyRound,
    title: "Connect credentials",
    text: "Save exchange API credentials once. Secrets stay encrypted and are used only for account reads and signed order actions.",
  },
  {
    icon: SlidersHorizontal,
    title: "Set DCA rules",
    text: "Control entry ladders, exit ladders, leverage, direction bias, order type, and reset frequency per contract.",
  },
  {
    icon: RefreshCcw,
    title: "Reset on schedule",
    text: "Auto Run can clean and rebuild the long or short DCA plan on the interval you choose.",
  },
  {
    icon: Layers3,
    title: "Scale by contract",
    text: "One membership includes one active bot. Add extra bot quantity when you want more active coins.",
  },
];

const integrations = [
  { name: "Apex Omni", status: "Live", detail: "Account reads, market config, order cleanup, signed placement" },
  { name: "Additional perpetual exchanges", status: "Planned", detail: "The data model already tracks trading platform and contract metadata" },
  { name: "Portfolio-level routing", status: "Roadmap", detail: "Future exchange adapters can share the same DCA planner surface" },
];

const guardrails = [
  "Active membership gate for Auto Run",
  "Server-side bot entitlement limits",
  "Encrypted API credentials",
  "Idempotent order actions",
  "Admin-only operational tooling",
  "Live-trading kill switch",
];

function App() {
  return (
    <main>
      <nav className="nav-shell" aria-label="Primary">
        <a className="brand" href="#top" aria-label="TradeBotSecrets home">
          <img className="brand-logo" src="/assets/tradebotsecrets-logo.jpg" alt="" aria-hidden="true" />
          <span>TradeBotSecrets</span>
        </a>
        <div className="nav-links">
          <a href="#how">How it works</a>
          <a href="#integrations">Integrations</a>
          <a href="#pricing">Pricing</a>
          <a href="#security">Security</a>
        </div>
        <a className="nav-cta" href={appUrl}>
          Open app <ArrowRight size={18} aria-hidden="true" />
        </a>
      </nav>

      <section className="hero" id="top">
        <img
          className="hero-image"
          src="/assets/tradebotsecrets-hero-dashboard.png"
          alt="A premium trading automation dashboard displayed on a laptop"
        />
        <div className="hero-content">
          <p className="eyebrow">DCA automation for active crypto traders</p>
          <h1>Reset long and short trading plans without rebuilding every order by hand.</h1>
          <p className="hero-copy">
            TradeBotSecrets turns your DCA settings into repeatable order workflows with membership-gated automation,
            per-contract bot limits, and exchange integration plumbing built for growth.
          </p>
          <div className="hero-actions">
            <a className="button primary" href={membershipUrl}>
              Start membership <ArrowRight size={19} aria-hidden="true" />
            </a>
            <a className="button secondary" href={appUrl}>
              Open DCA tool
            </a>
          </div>
          <div className="trust-strip" aria-label="Platform highlights">
            <span><ShieldCheck size={18} aria-hidden="true" /> Encrypted credentials</span>
            <span><Bot size={18} aria-hidden="true" /> Auto Run scheduler</span>
            <span><Coins size={18} aria-hidden="true" /> Extra bots scale linearly</span>
          </div>
        </div>
      </section>

      <section className="section split" id="how">
        <div>
          <p className="eyebrow">Built around the real workflow</p>
          <h2>From one coin to a managed bot stack.</h2>
          <p>
            The product is organized around contracts, not generic presets. A user can load a saved contract, adjust
            entry and exit logic, turn Auto Run on, and let the server enforce the subscription limit.
          </p>
        </div>
        <div className="workflow-grid">
          {workflow.map((item) => {
            const Icon = item.icon;
            return (
              <article className="feature-card" key={item.title}>
                <span className="icon-box"><Icon size={22} aria-hidden="true" /></span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section product-band">
        <div className="product-panel">
          <div className="panel-topline">
            <span>Bot Manager</span>
            <span>3 of 5 active</span>
          </div>
          <div className="bot-row active"><span>BTC-USDT</span><strong>24 Hour Auto Run</strong></div>
          <div className="bot-row active"><span>ETH-USDT</span><strong>12 Hour Auto Run</strong></div>
          <div className="bot-row active"><span>SOL-USDT</span><strong>6 Hour Auto Run</strong></div>
          <div className="bot-row"><span>Available slots</span><strong>2 remaining</strong></div>
        </div>
        <div>
          <p className="eyebrow">Product surface</p>
          <h2>Membership and automation limits are visible before the user saves.</h2>
          <p>
            Extra bots map cleanly to active contracts. The interface can show how many slots are available, while the
            server blocks saves that exceed the customer’s paid entitlement.
          </p>
          <a className="text-link" href={membershipUrl}>Manage bot quantity <ArrowRight size={18} aria-hidden="true" /></a>
        </div>
      </section>

      <section className="section" id="integrations">
        <div className="section-heading">
          <p className="eyebrow">Integrations</p>
          <h2>Apex Omni is live. The next step is a formal exchange adapter layer.</h2>
          <p>
            The current code stores a trading platform per setting and has platform contract metadata. Execution is
            still Apex-specific, so the front page should position future exchanges as roadmap support until adapters
            are implemented and tested.
          </p>
        </div>
        <div className="integration-grid">
          {integrations.map((item) => (
            <article className="integration-card" key={item.name}>
              <div>
                <h3>{item.name}</h3>
                <p>{item.detail}</p>
              </div>
              <span className={item.status === "Live" ? "status live" : "status planned"}>{item.status}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="section pricing" id="pricing">
        <div>
          <p className="eyebrow">Simple scaling</p>
          <h2>One bot included. Add more coins as you need them.</h2>
          <p>
            The billing model stays linear: the base subscription includes one active auto-run contract, and extra bots
            add monthly quantity for additional active contracts.
          </p>
        </div>
        <div className="price-card">
          <div className="price-line"><span>DCA membership</span><strong>$30/mo</strong></div>
          <div className="price-line"><span>Included active bot</span><strong>1 coin</strong></div>
          <div className="price-line"><span>Extra active bots</span><strong>$5/mo each</strong></div>
          <a className="button primary full" href={membershipUrl}>Start membership</a>
        </div>
      </section>

      <section className="section security" id="security">
        <div className="section-heading">
          <p className="eyebrow">Operational guardrails</p>
          <h2>Designed for automation with controls that matter.</h2>
        </div>
        <div className="guardrail-grid">
          {guardrails.map((item) => (
            <div className="guardrail" key={item}>
              <Check size={20} aria-hidden="true" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="cta-section">
        <Sparkles size={26} aria-hidden="true" />
        <h2>Ready to run DCA resets from a cleaner control surface?</h2>
        <p>Open the app, review your bot slots, and enable Auto Run on the contracts you want to manage.</p>
        <div className="hero-actions center">
          <a className="button primary" href={appUrl}>Open app <ArrowRight size={19} aria-hidden="true" /></a>
          <a className="button secondary" href={membershipUrl}>Manage membership</a>
        </div>
      </section>

      <footer>
        <span className="footer-brand">
          <img className="brand-logo small" src="/assets/tradebotsecrets-logo.jpg" alt="" aria-hidden="true" />
          TradeBotSecrets
        </span>
        <span>Trading involves risk. Automation does not guarantee profit.</span>
      </footer>
    </main>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
